import * as d3 from "d3"
import "d3-drag"
import * as resizers from "./resizers"
import * as rect from "./rect"
import * as ellipse from "./ellipse"
import * as html from "./html"
import * as shadow from "./shadow"
import { v4 as uuidV4 } from "uuid"
import { StyleBarState } from "app/core/components/sidebars/StyleBar"
import { applyStyleBarOption } from "app/helpers/StyleOptionTranslator"
import * as select from "./select"
import * as data from "./data"
import * as screen from "./screen"
import * as image from "./image"
import { ButtonSelection } from "./types"
import * as unreadBadge from "./unreadBadge"
import * as mousetrap from "./mousetrap"
import * as params from "./params"
import { buttons, nativeSvgElement } from "./svg"
import { convertSVGPointToScreenCoordinates } from "./screen"

export const append = (x?: number, y?: number, width?: number, height?: number) => {
  const buttonWidth = width ?? params.defaultButtonWidth
  const buttonHeight = height ?? params.defaultButtonHeight
  const button = d3
    .select("#svg-dashboard")
    .append("g")
    .classed("playerdash-dashboard-button", true)
    .datum({
      x: screen.constrainX(x ?? 100, buttonWidth),
      y: screen.constrainY(y ?? 100, buttonHeight),
      width: buttonWidth,
      height: buttonHeight,
      uuid: uuidV4(),
    })
  computeAttributes(button)

  // append the shadow before rect and ellipse,
  // so that it can be resized at the same time
  shadow.append(button)
  rect.append(button)
  image.append(button)
  ellipse.append(button)
  mousetrap.append(button)
  // text.append(button)
  html.append(button)
  resizers.append(button)
  unreadBadge.append(button)

  return button
}

export const changeType = (button: any, buttonType: StyleBarState["buttonType"]) => {
  applyStyleBarOption(button)("buttonType", buttonType)
}

export const createFromDummyData = (svgElement) => {
  let groupElements = svgElement.selectAll("g.playerdash-dashboard-button")

  groupElements.join(
    (enter) =>
      enter
        .append("g")
        .classed("playerdash-dashboard-button", true)
        .attr("uuid", (d) => d.uuid),
    (update) => update.attr("uuid", (d) => d.uuid),
    (exit) => exit.remove(),
  )
  computeAttributes(groupElements)
  return groupElements
}

export const createFromOuterHTML = (svgElement, outerHTML: string) => {
  const svgDashboardElement = document.querySelector("#svg-dashboard")
  const existingButtonsData = buttons().data()
  const existingSelectionData = select.getSelection()?.data()
  svgDashboardElement?.insertAdjacentHTML("beforeend", outerHTML)
  data.computeFromAttributes()
  const newButtonsData = buttons().data().slice(existingButtonsData.length)
  newButtonsData.forEach((data) => {
    const selectedDatum = existingSelectionData?.filter((d) => d.uuid === data.uuid)
    data.uuid = uuidV4()
    const newX = selectedDatum?.length === 1 ? selectedDatum[0].x : data.x
    data.x = screen.constrainX(newX + 50, data.width)
    const newY = selectedDatum?.length === 1 ? selectedDatum[0].y : data.y
    data.y = screen.constrainY(newY + 50, data.height)
  })
  const newButtons = buttons().filter((_, index) => index >= existingButtonsData.length)
  newButtons.data(newButtonsData)
  // FIXME: some of the "computeAttributes" functions do not yet support
  //        multi element selection
  newButtons.each(function () {
    computeAllAttributes(d3.select(this))
  })
  return newButtons
}

export const computeAttributes = (buttonElements) => {
  buttonElements
    .attr("x", (d) => d.x)
    .attr("y", (d) => d.y)
    .attr("width", (d) => d.width)
    .attr("height", (d) => d.height)
    .attr("transform", (d) => `translate(${d.x},${d.y})`)
    .attr("uuid", (d) => d.uuid)
}

// when the button has been loaded into the DOM,
// we need to set the datum manually
export const setDatum = (buttonElements) => {
  buttonElements.each(function () {
    const button = d3.select(this) as unknown as ButtonSelection
    const x = Number(button.attr("x") ?? 100)
    const y = Number(button.attr("y") ?? 100)
    const width = Number(button.attr("width"))
    const height = Number(button.attr("height"))
    const uuid = button.attr("uuid")
    button.datum({
      x: screen.constrainX(x, width),
      y: screen.constrainY(y, height),
      width,
      height,
      uuid,
    })
  })
}

export const updatePosition = (buttonElements) => {
  buttonElements
    .attr("x", (d) => d.x)
    .attr("y", (d) => d.y)
    .attr("transform", (d) => `translate(${d.x},${d.y})`)
}

export const updateAfterResize = (buttonElements) => {
  buttonElements
    .attr("x", (d) => d.x)
    .attr("y", (d) => d.y)
    .attr("width", (d) => d.width)
    .attr("height", (d) => d.height)
    .attr("transform", (d) => `translate(${d.x},${d.y})`)
}

export const computeAllAttributes = (buttonElements) => {
  computeAttributes(buttonElements)
  rect.computeAttributes(buttonElements)
  ellipse.computeAttributes(buttonElements)
  image.computeAttributes(buttonElements)
  html.computeAttributes(buttonElements)
  resizers.computeAttributes(buttonElements)
  unreadBadge.computeAttributes(buttonElements)
  shadow.computeAttributes(buttonElements)
  mousetrap.computeAttributes(buttonElements)
}

export const clone = (buttonElement) => {
  const clonedElement = buttonElement?.clone(true)
  clonedElement?.each(function () {
    d3.select(this).attr("uuid", uuidV4())
  })
  data.computeFromAttributes()

  if (clonedElement) {
    clonedElement.datum((d) => ({
      ...d,
      x: screen.constrainX(d.x + 50, d.width),
      y: screen.constrainY(d.y + 50, d.height),
    }))
    updatePosition(clonedElement)
  }
  select.button(clonedElement)

  return clonedElement
}

export const withUuid = (uuid: string): ButtonSelection => {
  return d3.select(`g.playerdash-dashboard-button[uuid="${uuid}"]`)
}

export const isButton = (element?: ButtonSelection): boolean => {
  return element?.classed("playerdash-dashboard-button") ?? false
}

export const lower = (element) => {
  element?.lower()
  d3.select("#dashboard-background")?.lower()
}

export const raise = (element) => {
  element?.raise()
}

export const getButtonType = (
  buttonElement: ButtonSelection,
): "rect" | "ellipse" | "text" | "image" | undefined => {
  const rectElement = buttonElement.select(".dashboard-button-rect")
  if (!!rectElement?.size() && rectElement.style("display") === "inline") {
    return "rect"
  }
  const ellipseElement = buttonElement.select(".dashboard-button-ellipse")
  if (!!ellipseElement?.size() && ellipseElement.style("display") === "inline") {
    return "ellipse"
  }
  const imageElement = buttonElement.select(".dashboard-button-image")
  if (!!imageElement?.size() && imageElement.style("display") === "inline") {
    return "image"
  }
  const textElement = buttonElement.select(".dashboard-button-html-container")
  if (!!textElement?.size() && textElement.style("display") !== "none") {
    return "text"
  }
  return undefined
}

export const getDOMPosition = (buttonElement: ButtonSelection): DOMRect | undefined => {
  const position = buttonElement?.node()?.getBoundingClientRect()
  const svg = nativeSvgElement()
  if (!buttonElement || !position || !svg) {
    return
  }
  return convertSVGPointToScreenCoordinates(
    svg,
    new DOMRect(
      Number(buttonElement.attr("x") ?? 0),
      Number(buttonElement.attr("y") ?? 0),
      Number(buttonElement.attr("width") ?? 300),
      Number(buttonElement.attr("height") ?? 200),
    ),
  )
}
